import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap';
import '../../style.css';

const Main = () => {
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [textQuery, setTextQuery] = useState("");
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
            setMatches([]);
            setError(null);
        }
    };

    const handleTextChange = (e) => {
        setTextQuery(e.target.value);
    };

    const clearImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
    };

    const handleViewImage = async (url) => {
        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error('Network response was not ok');
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');
        } catch (err) {
            console.error('Error opening image:', err);
            window.open(url, '_blank'); // Fallback
        }
    };

    const findMatches = async (e) => {
        if (e) e.preventDefault();
        if (!selectedImage && !textQuery.trim()) return;

        setError(null);
        setLoading(true);
        const formData = new FormData();
        if (selectedImage) {
            formData.append('image', selectedImage);
        }
        if (textQuery.trim()) {
            formData.append('text_query', textQuery);
        }

        try {
            const response = await fetch('/api/find_match', {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                throw new Error('Failed to fetch matches');
            }

            const data = await response.json();
            setMatches(data.points);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const findMatchesWithXimilar = async (e) => {
        if (e) e.preventDefault();
        if (!selectedImage && !textQuery.trim()) return;

        setError(null);
        setLoading(true);
        setMatches([]);

        const formData = new FormData();
        if (selectedImage) {
            formData.append('image', selectedImage);
        }
        if (textQuery.trim()) {
            formData.append('text_query', textQuery);
        }

        try {
            const response = await fetch('/api/ximilar_search', {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.detail || 'Failed to fetch matches from Ximilar');
            }

            const data = await response.json();
            const mappedResults = (data.results || []).map((rec) => ({
                payload: {
                    image_name: rec.image_name,
                    image_path: rec.path,
                }
            }));
            setMatches(mappedResults);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };
    return (
        <Container>
            {/* Search Section */}
            <Row className="justify-content-center mb-5">
                <Col md={8} lg={6}>
                    <div className="text-center mb-4">
                        <h2 className="fw-bold">Visual Search</h2>
                        <p className="text-muted">Search by text, image, or both together.</p>
                    </div>

                    <div className="search-container shadow-sm p-2 mb-4 bg-white rounded-pill d-flex align-items-center border">
                        <input 
                            type="text" 
                            className="border-0 outline-none bg-transparent px-4 py-2" 
                            placeholder="Describe jewelry... (e.g. 'diamond ring')" 
                            value={textQuery}
                            onChange={handleTextChange}
                            onKeyDown={(e) => e.key === 'Enter' && findMatchesWithXimilar()}
                            disabled={loading}
                        />
                        <Button 
                            className="btn-primary-custom rounded-pill px-4"
                            onClick={findMatchesWithXimilar}
                            disabled={loading || (!textQuery.trim() && !selectedImage)}>
                            {loading && textQuery.trim() && !selectedImage ? <span className='d-flex align-items-center'><Spinner animation="border" size="sm" className='me-2'/>Searching</span> : 'Search'}
                        </Button>
                    </div>

                    <div className="text-center mb-3">
                        <span className="text-muted small fw-bold">OR</span>
                    </div>

                    <div className="upload-dropzone">
                        <input 
                            type="file" 
                            accept="image/*" 
                            onChange={handleImageChange} 
                            disabled={loading}
                        />
                        <div className="py-2">
                            {imagePreview ? (
                                <img 
                                    src={imagePreview} 
                                    alt="Preview" 
                                    style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '8px' }} 
                                />
                            ) : (
                                 <div className="text-muted">
                                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>+</div>
                                    <p className="mb-0">Click or drag image to upload</p>
                                    <small>JPG, PNG or WEBP</small>
                                </div>
                            )}
                        </div>
                    </div>

                    {imagePreview && (
                        <div className="text-center mt-2">
                            <Button variant="outline-secondary" size="sm" onClick={clearImage} disabled={loading}>
                                ✕ Remove Image
                            </Button>
                        </div>
                    )}

                    {selectedImage && (
                        <div className="text-center mt-3">
                            <Button 
                                onClick={findMatchesWithXimilar} 
                                disabled={loading}
                                className="btn-primary-custom px-4">
                                {loading ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Searching...
                                    </>
                                ) : 'Find Matches'}
                            </Button>
                        </div>
                    )}
                </Col>
            </Row>

            {/* Error Message */}
            {error && (
                <Row className="justify-content-center mb-4">
                    <Col md={12}>
                        <Alert variant="danger" className="text-center">
                            {error}
                        </Alert>
                    </Col>
                </Row>
            )}

            {/* Results Section */}
            {matches.length > 0 && (
                <div className="mt-5">
                    <h4 className="section-title mb-4">Search Results</h4>
                    <Row xs={1} sm={2} md={3} lg={4} className="g-4">
                        {matches.map((match, index) => {
                            // const imgPath = match.payload?.image_path;
                            const imgName = match.payload?.image_name;
                            return (
                                <Col key={index}>
                                    <Card className="match-card h-100 shadow-sm p-3">
                                        <Card.Img variant="top" style={{ cursor: 'pointer' }} src={`/api/Images/${imgName}`} onClick={()=>handleViewImage(`/api/Images/${imgName}`)}/>
                                        <Card.Body className="p-3">
                                            {/* <Card.Title className="fs-6 fw-bold mb-0 text-truncate">
                                                Image Match #{index + 1}
                                            </Card.Title> */}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                </div>
            )}
            
            {!loading && matches.length === 0 && (selectedImage || textQuery) && !error && (
               <div className="text-center py-5 text-muted">
                    <p>No matches found yet. Try adjusting your search.</p>
               </div>
            )}

        </Container>
    );
};

export default Main;
